import React from 'react';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import UniversityVerifiedBadge from '../components/university/UniversityVerifiedBadge';

const TrademarkDisclaimer = () => {
  useDocumentMetadata(
    'Trademark & Nominative Fair Use Policy - Dakhla',
    'Learn about Dakhla\'s nominative fair use policy, university logo usage, and intellectual property rights under Pakistani law.'
  );

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200/80 p-8 sm:p-12">
        {/* Header */}
        <div className="border-b border-slate-100 pb-8 mb-8">
          <div className="text-sm font-bold uppercase tracking-wider text-primary-600 mb-2">
            Intellectual Property & Fair Use
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            Trademark & Directory Disclaimer
          </h1>
          <p className="mt-2 text-slate-500 text-sm">
            Legal policy governing university names, logos, and directory listings under Pakistani intellectual property laws.
          </p>
        </div>

        {/* Disclaimer Body */}
        <div className="prose prose-slate max-w-none space-y-8 text-slate-700 leading-relaxed">
          <section className="bg-slate-50 border border-slate-200/90 rounded-xl p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-2">
              Nominative Fair Use Notice
            </h2>
            <p className="text-slate-600 text-sm leading-normal">
              All university names, crests, logos, and trademarks displayed across Dakhla belong exclusively to their respective registered owners. Dakhla displays these marks solely for identification, educational indexing, and factual directory reference under the principles of <strong>Nominative Fair Use</strong> in accordance with the <strong>Trade Marks Ordinance, 2001</strong> and the <strong>Competition Act, 2010</strong> of Pakistan.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              Directory Classification & Badging System
            </h2>
            <p className="mb-4">
              To ensure full transparency for students and higher education institutions, Dakhla clearly distinguishes between partnered portals and public information listings:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Partner Card */}
              <div className="border border-indigo-100 bg-indigo-50/50 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2 font-bold text-slate-900">
                  <span>Official Partner Institution</span>
                  <UniversityVerifiedBadge isPartner={true} className="h-5 w-5" />
                </div>
                <p className="text-xs text-slate-600 leading-normal">
                  Institutions with active partnership agreements with Dakhla. Direct applications, verified fee schedules, and real-time updates are processed directly through our secure platform.
                </p>
              </div>

              {/* Non-Partner Directory Card */}
              <div className="border border-slate-200 bg-slate-50/70 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2 font-bold text-slate-900">
                  <span>Public Information Listing</span>
                  <UniversityVerifiedBadge isPartner={false} showNonPartnerIcon={true} className="h-5 w-5" />
                </div>
                <p className="text-xs text-slate-600 leading-normal">
                  Publicly indexed profiles compiled from official university prospectuses and public domain records to assist students in exploring options nationwide. Display does not imply endorsement or official affiliation.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">
              Institutional Rights & Takedown Requests
            </h2>
            <p>
              If you represent an educational institution listed on Dakhla and would like to update your official profile, claim your partner badge, or request a review of your listed materials, please reach out to our institutional relations team:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Institutional & Legal Desk:</strong> <a href="mailto:support.dakhla@gmail.com" className="text-primary-600 font-semibold hover:underline">support.dakhla@gmail.com</a></li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TrademarkDisclaimer;
